// Iconify
import "iconify-icon";

// Bootstrap Collapse (used to close the mega menu programmatically without
// dispatching a synthetic click that other widgets' outside-click handlers would catch)
import Collapse from "bootstrap/js/dist/collapse";

// Site-wide JS (Safe to run immediately; documentElement is always available)
document.documentElement.classList.add("motorola-shell");

(() => {
  const header = document.querySelector(".site-header");

  if (!header) {
    return;
  }

  const setHeaderHeightVar = () => {
    document.documentElement.style.setProperty(
      "--site-header-height",
      `${header.offsetHeight}px`,
    );
  };

  setHeaderHeightVar();
  window.addEventListener("resize", setHeaderHeightVar);
  window.addEventListener("load", setHeaderHeightVar);
})();

// Add target="_blank" to external links. Internal links are the current
// site itself, any sibling on the same network domain (e.g. sgwglobal.dev-wp.dev
// and motorola.dev-wp.dev share "dev-wp.dev"), or the motorola*.com family
// of separately-domained sites (motorola.com, motorolasound.com, etc.).
document.addEventListener("DOMContentLoaded", () => {
  const currentHostname = window.location.hostname;
  const currentDomainSuffix = currentHostname.split(".").slice(-2).join(".");

  document.querySelectorAll('a[href^="http"]').forEach((link) => {
    try {
      const hostname = new URL(link.href).hostname;
      const isSameSite = hostname === currentHostname;
      const isSiblingSite =
        hostname === currentDomainSuffix ||
        hostname.endsWith(`.${currentDomainSuffix}`);
      const isBrandFamily = /motorola.*\.(com|dev|local)$/i.test(hostname);

      if (!isSameSite && !isSiblingSite && !isBrandFamily) {
        link.setAttribute("target", "_blank");
      }
    } catch {
      // Ignore links with malformed URLs
    }
  });
});

// Wait for the DOM to be fully loaded
document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll(".js-card-clickable").forEach((card) => {
    const link = card.querySelector("a[href]");

    if (!link) {
      return;
    }

    card.addEventListener("click", (event) => {
      if (event.target.closest("a, button")) {
        return;
      }

      if (window.getSelection && String(window.getSelection())) {
        return;
      }

      link.click();
    });
  });

  // Product archive filter drawer
  const filterDrawer = document.getElementById("filterDrawer");
  if (filterDrawer) {
    const minRange = filterDrawer.querySelector(".thumb-min");
    const maxRange = filterDrawer.querySelector(".thumb-max");
    const fill = filterDrawer.querySelector(".price-slider-fill");
    const minInput = filterDrawer.querySelector(".input-min");
    const maxInput = filterDrawer.querySelector(".input-max");
    const pricePresets = filterDrawer.querySelectorAll(".JS-preset-price");
    const categoryInputs = filterDrawer.querySelectorAll(
      'input[name="category"]',
    );

    // Automatically parse constraints from inputs rather than hardcoding variables
    const sliderMin = minRange ? Number(minRange.min) : 0;
    const sliderMax = maxRange ? Number(maxRange.max) : 300;
    const gap = 5;

    const getPercent = (value) =>
      ((value - sliderMin) / (sliderMax - sliderMin)) * 100;

    const rootFontSize =
      parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const thumbSize = 1.15 * rootFontSize;

    const updateSliderFill = () => {
      if (!fill || !minRange || !maxRange) return;
      const minRatio = getPercent(Number(minRange.value)) / 100;
      const maxRatio = getPercent(Number(maxRange.value)) / 100;

      fill.style.left = `calc(${minRatio * 100}% + ${
        thumbSize / 2 - minRatio * thumbSize
      }px)`;
      fill.style.right = `calc(${(1 - maxRatio) * 100}% + ${
        maxRatio * thumbSize - thumbSize / 2
      }px)`;
    };

    const syncFieldsFromSlider = () => {
      if (minInput)
        minInput.value =
          Number(minRange.value) > sliderMin ? minRange.value : "";
      if (maxInput)
        maxInput.value =
          Number(maxRange.value) < sliderMax ? maxRange.value : "";
    };

    const evaluateActivePresets = () => {
      if (!minRange || !maxRange) return;
      const lo = Number(minRange.value);
      const hi = Number(maxRange.value);

      pricePresets.forEach((btn) => {
        const btnMin = Number(btn.dataset.min);
        const btnMax = Number(btn.dataset.max);
        btn.classList.toggle("is-active", btnMin === lo && btnMax === hi);
      });
    };

    const refreshSliderState = () => {
      updateSliderFill();
      evaluateActivePresets();
    };

    // Range Slider Inputs Interaction Handlers
    if (minRange && maxRange) {
      minRange.addEventListener("input", () => {
        if (Number(minRange.value) > Number(maxRange.value) - gap) {
          minRange.value = Number(maxRange.value) - gap;
        }
        syncFieldsFromSlider();
        refreshSliderState();
      });

      maxRange.addEventListener("input", () => {
        if (Number(maxRange.value) < Number(minRange.value) + gap) {
          maxRange.value = Number(minRange.value) + gap;
        }
        syncFieldsFromSlider();
        refreshSliderState();
      });
    }

    // Explicit typing inside programmatic manual text blocks
    const clampValue = (val) => Math.min(Math.max(val, sliderMin), sliderMax);

    minInput?.addEventListener("input", () => {
      const parsedVal = clampValue(Number(minInput.value) || sliderMin);
      if (minRange && maxRange) {
        minRange.value = Math.min(parsedVal, Number(maxRange.value) - gap);
      }
      refreshSliderState();
    });

    maxInput?.addEventListener("input", () => {
      const parsedVal =
        maxInput.value === "" ? sliderMax : clampValue(Number(maxInput.value));
      if (minRange && maxRange) {
        maxRange.value = Math.max(parsedVal, Number(minRange.value) + gap);
      }
      refreshSliderState();
    });

    // Quick Option Preset Pill Clickers
    pricePresets.forEach((btn) => {
      btn.addEventListener("click", () => {
        if (!minRange || !maxRange) return;
        minRange.value = clampValue(Number(btn.dataset.min));
        maxRange.value = clampValue(Number(btn.dataset.max));
        syncFieldsFromSlider();
        refreshSliderState();
      });
    });

    // Category Radio Selection Active State Toggle (styling driven by .is-active in CSS)
    const updateCategoryHighlight = () => {
      categoryInputs.forEach((radio) => {
        radio
          .closest(".price-preset")
          ?.classList.toggle("is-active", radio.checked);
      });
    };

    categoryInputs.forEach((radio) => {
      radio.addEventListener("change", updateCategoryHighlight);
    });

    updateCategoryHighlight();

    // Filter Form Submission - Build Pretty Permalink URL with Category + Other Filters
    const filterForm = filterDrawer?.querySelector("form");
    if (filterForm) {
      filterForm.addEventListener("submit", function (e) {
        e.preventDefault();

        const formData = new FormData(this);
        const category = formData.get("category") || "";
        const minPrice = formData.get("min_price") || "";
        const maxPrice = formData.get("max_price") || "";
        const features = formData.getAll("features[]") || [];
        const isShopContext = this.dataset.context !== "support";

        // Use the form's action URL as the base (falls back to current path)
        let base =
          this.getAttribute("action") ||
          window.location.pathname ||
          "/products";
        try {
          const parsed = new URL(base, window.location.origin);
          base = parsed.pathname.replace(/\/$/, "");
        } catch (err) {
          base = String(base).replace(/\/$/, "");
        }

        let newUrl = base || "/products";

        const params = new URLSearchParams();

        if (category) {
          if (isShopContext) {
            newUrl += "/category/" + encodeURIComponent(category);
          } else {
            params.append("category", category);
          }
        }

        if (minPrice) params.append("min_price", minPrice);
        if (maxPrice) params.append("max_price", maxPrice);
        features.forEach((f) => params.append("features[]", f));

        const queryString = params.toString();
        if (queryString) {
          newUrl += "?" + queryString;
        }

        // Make URL absolute when it's a pathname
        if (!/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(newUrl)) {
          newUrl = window.location.origin + newUrl;
        }

        window.location.href = newUrl;
      });
    }

    // Run synchronization during initial boot cycle layout parse passes
    if (minInput && minInput.value && minRange) {
      minRange.value = clampValue(Number(minInput.value));
    }
    if (maxInput && maxInput.value && maxRange) {
      maxRange.value = clampValue(Number(maxInput.value));
    }
    refreshSliderState();
  }

  // On scroll, add an active tracking state class to the header
  const siteHeader = document.querySelector(".site-header");

  function updateHeaderState() {
    siteHeader?.classList.toggle("is-scrolled", window.scrollY > 0);
  }

  // Passive listener ensures high-performance frame execution during scrolling
  window.addEventListener("scroll", updateHeaderState, { passive: true });
  updateHeaderState();

  // Mega Menu Logic, Toggle Exit, and Header State Management
  const megaMenuElement = document.getElementById("motorolaMegaMenu");
  const navbarToggler = document.querySelector(".navbar-toggler");

  if (megaMenuElement) {
    const previewImage = megaMenuElement.querySelector("#megaMenuPreviewImage");
    const menuItems = megaMenuElement.querySelectorAll(".mega-menu-item");

    let animationTimeout = null;
    if (previewImage && menuItems.length > 0) {
      menuItems.forEach((item) => {
        item.addEventListener("mouseenter", function () {
          const newImageSrc = this.getAttribute("data-preview-image");

          if (newImageSrc && previewImage.getAttribute("src") !== newImageSrc) {
            clearTimeout(animationTimeout);

            previewImage.classList.add("is-switching");

            animationTimeout = setTimeout(() => {
              previewImage.setAttribute("src", newImageSrc);
              previewImage.setAttribute("alt", this.textContent.trim());

              if (previewImage.style.display === "none") {
                previewImage.style.display = "block";
              }

              previewImage.classList.remove("is-switching");
            }, 120);

            menuItems.forEach((el) => el.classList.remove("active"));
            this.classList.add("active");
          }
        });
      });
    }

    megaMenuElement.addEventListener("show.bs.collapse", () => {
      siteHeader?.classList.add("is-open");
      megaMenuElement.classList.add("is-opening");
    });

    megaMenuElement.addEventListener("shown.bs.collapse", () => {
      megaMenuElement.classList.remove("is-opening");
    });

    megaMenuElement.addEventListener("hide.bs.collapse", () => {
      siteHeader?.classList.remove("is-open");
      megaMenuElement.classList.remove("is-opening");
    });

    // Toggle exit and outside-clicks logic
    if (navbarToggler) {
      document.addEventListener("click", (event) => {
        const isMenuOpen =
          megaMenuElement.classList.contains("show") ||
          navbarToggler.getAttribute("aria-expanded") === "true";

        if (isMenuOpen) {
          const clickedInsideMenu = megaMenuElement.contains(event.target);
          const clickedToggleButton = navbarToggler.contains(event.target);

          if (!clickedInsideMenu && !clickedToggleButton) {
            Collapse.getOrCreateInstance(megaMenuElement).hide();
          }
        }
      });
    }
  }
});

// Remember a manually chosen language and restore it on a later visit.
//
// Deliberately client-side: the site is behind WP Rocket's full-page cache, so
// a server-side cookie redirect would either be bypassed or force the cache to
// vary by cookie. Only the home page redirects, so a shared deep link always
// opens in the language it was shared in, and because it needs the cookie,
// crawlers (which carry none) are never redirected.
(() => {
  const COOKIE = "motorola_lang";
  const MAX_AGE = 60 * 60 * 24 * 365;

  const langOf = (item) => {
    const match = Array.from(item.classList).find((name) =>
      /^wpml-ls-item-.+/.test(name),
    );

    return match ? match.replace("wpml-ls-item-", "") : null;
  };

  const readCookie = () => {
    const match = document.cookie.match(
      new RegExp(`(?:^|;\\s*)${COOKIE}=([^;]*)`),
    );

    return match ? decodeURIComponent(match[1]) : null;
  };

  const writeCookie = (name, value, { path = "/", domain = "" } = {}) => {
    const parts = [
      `${name}=${encodeURIComponent(value)}`,
      `max-age=${MAX_AGE}`,
      `path=${path}`,
      "samesite=lax",
    ];

    if (domain) {
      parts.push(`domain=${domain}`);
    }

    document.cookie = parts.join(";");
  };

  // Only an actual switch counts as a choice — clicking the current language
  // just opens the dropdown.
  document.addEventListener("click", (event) => {
    const link = event.target.closest(".wpml-ls-item a");
    const item = link?.closest(".wpml-ls-item");

    if (!item || item.classList.contains("wpml-ls-current-language")) {
      return;
    }

    const code = langOf(item);

    if (!code) {
      return;
    }

    writeCookie(COOKIE, code);

    // A manual choice must outrank WPML's automatic browser/geo redirect.
    // That redirect only runs while its own cookie is absent, so setting it
    // here retires it for good rather than leaving the two to fight.
    writeCookie(
      "_icl_visitor_lang_js",
      code,
      window.wpml_browser_redirect_params?.cookie ?? {},
    );
  });

  document.addEventListener("DOMContentLoaded", () => {
    const stored = readCookie();

    if (!stored || !document.body.classList.contains("home")) {
      return;
    }

    const current = document.querySelector(
      ".wpml-ls-item.wpml-ls-current-language",
    );

    if (!current || langOf(current) === stored) {
      return;
    }

    // The switcher's own href is the translated equivalent of this page, so
    // there's no URL structure to reproduce here.
    const target = document.querySelector(
      `.wpml-ls-item-${CSS.escape(stored)} a`,
    );

    if (target) {
      window.location.replace(target.href);
    }
  });
})();

(() => {
  const COOKIE = "motorola_product";
  const MAX_AGE = 60 * 60 * 24 * 7;

  const readCookie = () => {
    const match = document.cookie.match(
      new RegExp(`(?:^|;\\s*)${COOKIE}=([^;]*)`),
    );

    return match ? decodeURIComponent(match[1]) : null;
  };

  const writeCookie = (value) => {
    document.cookie = `${COOKIE}=${encodeURIComponent(value)};max-age=${MAX_AGE};path=/;samesite=lax`;
  };

  const fromUrl = new URLSearchParams(window.location.search).get(
    "moto_product",
  );
  const fromPage = document.querySelector("[data-moto-product]")?.dataset
    .motoProduct;

  if (fromPage) {
    writeCookie(fromPage);
  }

  if (fromUrl) {
    writeCookie(fromUrl);
  }

  const reference = fromUrl || readCookie();

  if (!reference) {
    return;
  }

  if (fromUrl) {
    document.querySelectorAll("a.region-contacts__card").forEach((link) => {
      const href = link.getAttribute("href");

      if (!href || href.startsWith("#")) {
        return;
      }

      const url = new URL(href, window.location.href);

      if (url.origin !== window.location.origin) {
        return;
      }

      url.searchParams.set("moto_product", reference);
      link.href = url.toString();
    });
  }

  const hidden = document.querySelector('input[name="moto_product"]');

  if (hidden) {
    hidden.value = reference;
  }

  const normalise = (text) => text.trim().replace(/\s+/g, " ").toLowerCase();

  const findOption = (select, wanted) => {
    const target = normalise(wanted);
    const options = Array.from(select.options).filter((opt) => opt.value);
    const exact = options.find(
      (opt) =>
        normalise(opt.value) === target || normalise(opt.text) === target,
    );

    if (exact) {
      return exact;
    }

    const partial = options.filter((opt) => {
      const name = normalise(opt.text);

      return name.startsWith(target) || target.startsWith(name);
    });

    return partial.length === 1 ? partial[0] : null;
  };

  const deviceSelect = document.querySelector('select[name="input_7"]');
  const match = deviceSelect ? findOption(deviceSelect, reference) : null;

  if (!match) {
    return;
  }

  deviceSelect.value = match.value;
  deviceSelect.dispatchEvent(new Event("change", { bubbles: true }));
})();

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll("[data-support-combobox]").forEach((form) => {
    const input = form.querySelector('[role="combobox"]');
    const list = form.querySelector('[role="listbox"]');
    const empty = form.querySelector(".support-combobox__empty");
    const toggle = form.querySelector(".support-combobox__toggle");
    const options = Array.from(form.querySelectorAll('[role="option"]'));
    let active = -1;

    const visible = () => options.filter((option) => !option.hidden);

    const setActive = (index) => {
      const items = visible();

      options.forEach((option) => option.setAttribute("aria-selected", "false"));
      active = items.length ? (index + items.length) % items.length : -1;

      if (active < 0) {
        input.removeAttribute("aria-activedescendant");
        return;
      }

      items[active].setAttribute("aria-selected", "true");
      items[active].scrollIntoView({ block: "nearest" });
      input.setAttribute("aria-activedescendant", items[active].id);
    };

    const setOpen = (open) => {
      list.hidden = !open;
      input.setAttribute("aria-expanded", open ? "true" : "false");
      toggle.setAttribute("aria-expanded", open ? "true" : "false");

      if (!open) {
        setActive(-1);
      }
    };

    const filter = () => {
      const term = input.value.trim().toLowerCase();

      options.forEach((option) => {
        option.hidden = !option.textContent.trim().toLowerCase().includes(term);
      });

      empty.hidden = visible().length > 0;
      setActive(-1);
    };

    const go = (option) => {
      if (option) {
        window.location.href = option.dataset.url;
      }
    };

    toggle.addEventListener("click", () => {
      if (list.hidden) {
        input.focus();
      } else {
        setOpen(false);
      }
    });

    input.addEventListener("focus", () => {
      filter();
      setOpen(true);
    });

    input.addEventListener("input", () => {
      filter();
      setOpen(true);
    });

    input.addEventListener("keydown", (event) => {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        setOpen(true);
        setActive(active + (event.key === "ArrowDown" ? 1 : -1));
      } else if (event.key === "Escape") {
        setOpen(false);
      }
    });

    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const items = visible();

      go(active >= 0 ? items[active] : items[0]);
    });

    list.addEventListener("mousedown", (event) => {
      event.preventDefault();
      go(event.target.closest('[role="option"]'));
    });

    document.addEventListener("click", (event) => {
      if (!form.contains(event.target)) {
        setOpen(false);
      }
    });
  });
});
